import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import type { SelfDescribingJson } from '@snowplow/browser-tracker'
import sanitizeItemContextMetadata from './eventContexts/item/sanitizeItemContextMetadata'

/**
 * Leaf builder for flight item Snowplow contexts.
 * Kept out of events.ts so cartFlightsContext can use it without
 * pulling helpers (and the cartFlightsContext ↔ events cycle).
 *
 * Member pricing fields mirror `ItemContextMetadata` rather than importing it — that import would
 * reinstate the cycle this module exists to avoid.
 */
interface FlightItemSnowplowContext extends Omit<comluxgroup.Item_1_1_2, 'categoryId' | 'productId'> {
  originAirportCode: string
  arrivalAirportCode: string
  departureDate: string
  arrivalDate?: string
  pricePerAdult: number
  airline: string
  fareType: string
  /**
   * Omitted, never zeroed, when there's no member price. The "member else public" idiom used by
   * `hotelRoomRateContext` is what puts `memberPrice: 0` in the warehouse, reading as 100% off.
   */
  memberPricePerAdult?: number
  nonMemberPricePerAdult?: number
  priceBasis?: 'round_trip' | 'per_bound'
  memberFareCount?: number
  selection?: 'carrier_deepest' | 'cheapest_member' | 'cheapest_public'
}

export function createFlightItemContext(data: FlightItemSnowplowContext): SelfDescribingJson {
  return comluxgroup.createItem_1_1_2({
    categoryId: 'flight',
    productId: 'flight',
    currency: data.currency,
    offerId: data.offerId,
    originCity: data.originCity,
    destinationCity: data.destinationCity,
    travelStart: data.departureDate,
    travelEnd: data.arrivalDate,
    duration: data.duration,
    numberOfAdults: data.numberOfAdults,
    numberOfChildren: data.numberOfChildren,
    price: data.pricePerAdult,
    originCountry: data.originCountry,
    destinationCountry: data.destinationCountry,
    metadata: sanitizeItemContextMetadata({
      airline: data.airline,
      originAirportCode: data.originAirportCode,
      arrivalAirportCode: data.arrivalAirportCode,
      memberPrice: data.memberPricePerAdult,
      nonMemberPrice: data.nonMemberPricePerAdult,
      priceBasis: data.priceBasis,
      memberFareCount: data.memberFareCount,
      selection: data.selection,
    }),
    typeId: data.fareType,
  })
}
